/**
 * Deploy-time settings for self-hosted Link (the image), read from `/config.json`
 * on the host serving Link, so one build can serve any customer's API. The image
 * writes it at startup from LINK_API_BASE_URL and LINK_COOKIE_DOMAIN:
 *
 *   { "apiBaseURL": "https://api.example.com", "cookieDomain": ".example.com" }
 *
 * Only self-hosted builds (REACT_APP_SELF_HOSTED) read it; Merge's CDN serves none.
 * The file is same-origin with Link, so the page embedding Link can't change it.
 */
type RuntimeConfig = {
  // The API this deploy serves, as an origin (the backend's BACKEND_URL).
  apiBaseURL?: string;
  // Domain for Link's cookies, which the API reads back. Unset: derived from
  // Link's host (see getLinkCookieDomain).
  cookieDomain?: string;
};

export const IS_SELF_HOSTED = process.env.REACT_APP_SELF_HOSTED === "true";

const DEFAULT_CONFIG: RuntimeConfig = {};

const COOKIE_DOMAIN = /^\.?[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

const parseConfig = (raw: unknown): RuntimeConfig => {
  const { apiBaseURL, cookieDomain } = (raw ?? {}) as {
    apiBaseURL?: unknown;
    cookieDomain?: unknown;
  };
  const config: RuntimeConfig = {};
  if (typeof apiBaseURL === "string") {
    try {
      config.apiBaseURL = new URL(apiBaseURL).origin;
    } catch {
      // Not a URL: no API.
    }
  }
  if (typeof cookieDomain === "string" && COOKIE_DOMAIN.test(cookieDomain)) {
    config.cookieDomain = cookieDomain;
  }
  return config;
};

let configPromise: Promise<RuntimeConfig> | undefined;
let loadedConfig: RuntimeConfig | undefined;

/**
 * Fetched once per page load, and only when something asks for it. A failed fetch
 * isn't kept: the iframe outlives one Link session, so the next open tries again
 * instead of failing until the page reloads.
 */
export const loadRuntimeConfig = (): Promise<RuntimeConfig> => {
  if (!configPromise) {
    configPromise = fetch("/config.json", { cache: "no-store" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`config.json: HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(parseConfig)
      .then((config) => {
        loadedConfig = config;
        return config;
      })
      .catch(() => {
        configPromise = undefined;
        return DEFAULT_CONFIG;
      });
  }
  return configPromise;
};

/**
 * The config if it has been loaded, for synchronous callers. Self-hosted Link
 * loads it before choosing its API, so it's here before any request or cookie.
 */
export const getLoadedRuntimeConfig = (): RuntimeConfig | undefined => loadedConfig;

/**
 * The API a self-hosted Link uses: always the one in config.json, never a Merge
 * host. `requested` is what the embedding page passed (undefined when it passed no
 * tenantConfig); anything other than the configured API fails closed, so a
 * misconfigured page can't send a customer's link token elsewhere.
 */
export const resolveSelfHostedApiBaseURL = (
  requested: string | undefined,
  config: RuntimeConfig
): { apiBaseURL: string } | { error: string } => {
  if (!config.apiBaseURL) {
    return { error: "Invalid apiBaseURL: config.json names no API for this Link deploy" };
  }
  const configured = `${config.apiBaseURL}/api`;
  if (!requested) {
    return { apiBaseURL: configured };
  }
  let requestedURL: URL;
  try {
    requestedURL = new URL(requested);
  } catch {
    return { error: `Invalid apiBaseURL, not a URL: ${requested}` };
  }
  if (`${requestedURL.origin}${requestedURL.pathname}` !== configured) {
    return { error: `Invalid apiBaseURL, not this deploy's API (${configured}): ${requested}` };
  }
  return { apiBaseURL: configured };
};
